import { ClipboardEvent, KeyboardEvent, RefObject, SyntheticEvent, useRef } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { getCursorPosition, isSafari, setCursorPosition } from 'service/browser';

type ContentEditableProps = {
  html: string;
  className?: string;
  placeholder?: string;
  onChange?: (text: string) => void;
  onEnter?: (value: string) => void;
  onBlur?: (value: string) => void;
  testId?: ELEMENT;
  inputRef?: RefObject<HTMLDivElement>;
};

/**
 * This component provides an editable DIV. Its HTML content is maintained through the 'html' prop,
 * and its text content will be reported through the optional functional prop 'onChange'. That
 * aligns with our needs, where we take the text content, mark it up as highlighted syntax, and
 * return it as HTML.
 *
 * @prop html           HTML content of the DIV
 * @prop className      class for the DIV; it has no styling of its own
 * @prop placeholder    placeholder text (shown when there is no content)
 * @prop onChange       function to call when the input changes; input is returned as text
 * @prop onEnter        function to call when the user presses the Enter key
 * @prop onBlur         function to call when the input loses focus
 * @prop testId         test ID for the editable DIV
 * @prop inputRef       ref for the editable DIV
 */
function ContentEditable({
  html,
  className,
  placeholder,
  onChange,
  onEnter,
  onBlur,
  testId,
  inputRef,
}: ContentEditableProps): JSX.Element {
  // handle to the DIV
  const localRef = useRef<HTMLDivElement>(null);
  const divRef = inputRef || localRef;
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Return the text content of the editable DIV. It represents spaces as character 160,
  // so convert those to regular spaces.
  const getInputText = (): string => {
    const input = divRef?.current;
    if (!input) {
      return '';
    }

    return (input.textContent ?? '').replace(/\xA0/g, ' ');
  };

  const handleBlur = (): void => {
    // workaround for Safari bug where blurring a content-editable DIV does not work; focus and blur a regular INPUT
    if (isSafari) {
      const textInput = hiddenInputRef.current as HTMLInputElement;
      if (textInput) {
        textInput.setSelectionRange(0, 0);
        textInput.blur();
      }
    }
    if (onBlur) {
      onBlur(getInputText());
    }
  };

  // Set the content of the editable DIV to the given HTML.
  const setInputHtml = (newHtml: string): void => {
    const input = divRef?.current;
    if (!input) {
      return;
    }

    let cursorPos = -1;
    const curText = getInputText();
    if (curText) {
      cursorPos = getCursorPosition(input);
      // if cursor is at the end (which it almost always is), make sure it stays there
      if (cursorPos === curText.length) {
        cursorPos = -1;
      }
    }
    input.innerHTML = newHtml;
    setCursorPosition(input, cursorPos);
  };

  // set the inner HTML of the DIV whenever the html prop changes value; also focus the DIV
  const prevHtml = usePrevious(html);
  useConditionalEffect(() => {
    setInputHtml(html);
    const input = divRef?.current;
    if (input) {
      input.focus();
    }
  }, html !== prevHtml);

  // notify the caller of any change to the content
  const handleInput = (event: SyntheticEvent): void => {
    const nativeEvent = event.nativeEvent as InputEvent;

    // When US international keyboard is on, keys such as ' is considered a composing character
    // so that users can type á by ' + a. We check if the key is composing. If so, don't do any
    // highlighting and let the user continue typing. Chrome and FireFox support isComposing
    // property from the event object but Safari doesn't. For Safari, check the inputType instead.
    if (nativeEvent.isComposing || nativeEvent.inputType === 'insertCompositionText') {
      return;
    }

    if (onChange) {
      onChange(getInputText());
    }
  };

  // handle Enter key
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (onEnter) {
      const key = event.keyCode;
      if (key === K.KEY_CODE.ENTER) {
        onEnter(getInputText());
        event.preventDefault();
      }
    }
  };

  // paste handler that allow to paste only text
  const pasteOnlyText = (e: ClipboardEvent<HTMLDivElement>): void => {
    const paste = e.clipboardData.getData('text');
    if (!paste) {
      e.preventDefault();
    }
  };

  const editableDiv = (
    <div
      ref={divRef}
      className={className}
      onInput={handleInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      contentEditable="true"
      spellCheck="false"
      autoCorrect="off"
      autoCapitalize="off"
      role="textbox"
      tabIndex={0}
      aria-label="Filter input"
      data-test-id={testId}
      onPaste={pasteOnlyText}
    />
  );

  if (isSafari) {
    return (
      <>
        {editableDiv}
        <input type="text" ref={hiddenInputRef} style={{ display: 'none' }} />
      </>
    );
  }

  return editableDiv;
}

export default ContentEditable;
